/* eslint-disable no-useless-escape */
import { HttpResponse } from '@angular/common/http';
import {
  ValidatorFn,
  AbstractControl,
  FormGroup,
  FormArray,
  FormControl,
  ValidationErrors,
} from '@angular/forms';
import * as CryptoJS from 'crypto-js';
import { takeUntil } from 'rxjs';
const esKey = 'and0LXMzY3VyMXR5LWszeQ==';

export class CommonValidations {
  static isValidText(value: any): boolean {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let result = false;
    if (this.isNotBlank(value)) {
      const repeatEx = /([\.\'\s\-])\1+/;
      const charsEx = /^[a-zA-Z.' \-]{1,}$/;
      result = !repeatEx.test(value) && charsEx.test(value);
    }
    return result;
  }

  static isNotBlank(text: string): boolean {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let result = false;
    if (typeof text !== 'undefined') {
      result = text.length > 0;
    }
    return result;
  }
}

export function decryptData(data: string) {
  try {
    const trimmedData = data.replace(/^"(.*)"$/, '$1');
    const parsedBase64Key = CryptoJS.enc.Base64.parse(esKey);
    const decryptedData = CryptoJS.AES.decrypt(trimmedData, parsedBase64Key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    if (decryptedData) {
      return decryptedData.toString(CryptoJS.enc.Utf8);
    }
    return trimmedData;
  } catch {
    return data;
  }
}

export function decryptSSN(data?: string) {
  if (data) {
    try {
      const trimmedData = data?.replace(/^"(.*)"$/, '$1');
      const parsedBase64Key = CryptoJS.enc.Base64.parse(esKey);
      const decryptedData = CryptoJS.AES.decrypt(trimmedData, parsedBase64Key, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
      });
      if (decryptedData) {
        return decryptedData.toString(CryptoJS.enc.Utf8);
      }
      return trimmedData;
    } catch {
      return data;
    }
  }
  return '';
}
export function encryptData(data?: string, key?: string) {
  try {
    const trimmedData = data ? data.replace(/^"(.*)"$/, '$1') : '';
    const parsedBase64Key = CryptoJS.enc.Base64.parse(key ? key : esKey);
    const encryptedData = CryptoJS.AES.encrypt(trimmedData, parsedBase64Key, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    if (encryptedData) {
      return btoa(encryptedData.toString());
    }
    return trimmedData;
  } catch (e) {
    return data;
  }
}
export function checkValidFirstLastName(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let result = false;
    const firstNameRegEx =
      /^(?!.*[ ]{2})(?!.*?["]{2})(?!.*?[']{2})(?!.*?[.]{2})(?!.*?[-]{2})[a-zA-Z "'.-]*$/;
    result = firstNameRegEx.test(control.value);
    return result ? null : { invalidName: true };
  };
}
export function flagValidator(flag: boolean | undefined): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null =>
    flag ? null : { invalidControl: true };
}
export function checkValidZipCode(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let result = false;
    const firstNameRegEx = /^\d{5}-\d{4}|\d{5}|[A-Z]\d[A-Z] \d[A-Z]\d$/;
    result = !control.value ? true : firstNameRegEx.test(control.value);
    return result ? null : { invalidZipCode: true };
  };
}

export function validateMI(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let result = false;
    if (null === control.value || control.value.trim().length === 0) {
      return null;
    }
    const splChrRegex =
      /^(\`|\~|\!|\@|\#|\$|\%|\^|\&|\*|\(|\)|\+|\=|\[|\{|\]|\}|\||\\|\'|\<|\,|\.|\>|\?|\|\""|\;|\:|\_|\"|\-|\s)$/;
    result =
      (CommonValidations.isValidText(control.value.trim()) &&
        !splChrRegex.test(control.value)) ||
      control.value === '';
    return result ? null : { invalidMI: true };
  };
}

export function validateCity(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let result = false;
    const cityRegEx =
      /^(?!.*[ ]{2})(?!.*?["]{2})(?!.*?[']{2})(?!.*?[.]{2})(?!.*?[-]{2})[a-zA-Z "'.-]*$/;
    result = cityRegEx.test(control.value);
    return result ? null : { invalidCity: true };
  };
}

export function validateMinimumAge(
  minAge: number,
  monthField = 'birthMonth',
  dayField = 'birthDay',
  yearField = 'birthYear',
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!(control instanceof FormGroup)) {
      return null;
    }

    const birthMonth = control.get(monthField)?.value;
    const birthDay = control.get(dayField)?.value;
    const birthYear = control.get(yearField)?.value;

    if (!birthMonth || !birthDay || !birthYear) {
      return null;
    }

    return isUnderMinimumAge(birthYear, birthMonth, birthDay, minAge)
      ? { ageLessThan18: true }
      : null;
  };
}

export function isUnderMinimumAge(
  birthYear: string | number,
  birthMonth: string | number,
  birthDay: string | number,
  minAge: number,
): boolean {
  const birthDate = new Date(
    Number(birthYear),
    Number(birthMonth) - 1,
    Number(birthDay),
  );

  if (Number.isNaN(birthDate.getTime())) {
    return false;
  }

  const minimumEligibleDate = new Date(
    birthDate.getFullYear() + minAge,
    birthDate.getMonth(),
    birthDate.getDate(),
  );

  return new Date() < minimumEligibleDate;
}

export function validateEmail(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let result = false;
    if (control.value && control.value.trim().length === 0) {
      return null;
    }
    const emailPattern =
      /^[a-zA-Z0-9.'_!*#$&|/'+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,25}$/;
    // TODO: in future can be enabled for adding restricting domains
    // const disAllowDomain: RegExp = /.(com|org|edu)$/;
    // result = emailPattern.test(control.value) && !disAllowDomain.test(control.value);
    result = emailPattern.test(control.value);
    return result ? null : { email: true };
  };
}

export function validatePhoneFormat(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (
      !control.value ||
      (control.value && control.value.trim().length === 0)
    ) {
      return null;
    }
    const phoneNumberRegEx = /^\(\d{3}\) \d{3}-\d{4}$/;
    return control.value.length === 14 && phoneNumberRegEx.test(control.value)
      ? null
      : { invalidPhoneNumber: true };
  };
}
export function isValidUserId(limit?: number): ValidatorFn {
  const tillLimit = limit ? limit.toString() : '32';
  return (control: AbstractControl): { [key: string]: any } | null => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let result = false;
    const userIdRegEx = new RegExp(
      `^(?=.*[A-Za-z])[a-zA-Z0-9]{4,${tillLimit}}$`,
      'i',
    );
    result = userIdRegEx.test(control.value);
    return result ? null : { invalidUserId: true };
  };
}

// triggers validation on all control by marking them touched
export function validateAllControls(form: FormGroup | FormArray) {
  for (const field of Object.keys(form.controls)) {
    const control = form.get(field);
    if (control instanceof FormControl) {
      control.markAsTouched({ onlySelf: true });
      // eslint-disable-next-line no-empty
      if (control.invalid) {
      }
    } else if (control instanceof FormGroup) {
      validateAllControls(control);
    }
  }
}

export function checkError(error: any) {
  let message: string;
  let status: number;
  if (
    typeof error.metadata.outcome.additionalDetails !== 'undefined' &&
    typeof error.metadata.outcome.message !== 'undefined' &&
    typeof error.metadata.outcome.code !== 'undefined'
  ) {
    message = `${error.metadata.outcome.message} ${error.metadata.outcome.code}`;
    status = error.metadata.outcome.status;
  } else {
    message = 'Internal Error';
    status = 500;
  }
  return {
    message,
    status,
    errObject: error,
  };
}
export function isArrayBufferResponse(
  response: any,
): response is HttpResponse<ArrayBuffer> {
  return (
    response instanceof HttpResponse && response.body instanceof ArrayBuffer
  );
}

export function checkErrorForStateToken(error: any) {
  let message: string;
  let status: number;
  if (error && error.status) {
    message = `${error?.error?.errorSummary || ''}`;
    status = error.status;
  } else {
    message = 'Internal Error';
    status = 500;
  }
  const errorObj = {
    status,
    type: 'error',
    message,
    code: status,
    developerMessage: message,
    moreInfo: '',
    errorCode: error.error?.errorCode,
    additionalDetails: error?.error?.errorCauses || [],
  };
  return {
    message,
    status,
    errObject: errorObj,
  };
}
export function phoneHideDetails(phoneNumber: string) {
  const cleaned = `${phoneNumber}`.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    return `(***)-***-${match[3]}`;
  }
  return '';
}

export function emailHideDetails(emailAddress: string) {
  if (!emailAddress) {
    return '';
  }
  const [name, domain] = emailAddress.split('@');
  return `${name.substr(0, 1)}**${name.slice(-1)}@${domain}`;
}

export function allowSpecificEmailDomain(email: string) {
  const emailPattern =
    /^[a-zA-Z0-9.'_!*#$&|/'+-]+@[a-zA-Z0-9.-]+\.(com|org|edu)$/i;
  if (!email) {
    return false;
  }
  return !emailPattern.test(email);
}

export function numberOnly($event: {
  which: number;
  keyCode: number;
}): boolean {
  const charCode = $event.which ? $event.which : $event.keyCode;
  if (charCode > 31 && (charCode < 48 || charCode > 57)) {
    return false;
  }
  return true;
}

export function selectRequiredValidator(ctrl: AbstractControl): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null =>
    control.value ? null : { required: { value: control.value } };
}

export function maskEmailAddress(email: string | undefined) {
  const mask = '***';
  if (email) {
    const at = email.indexOf('@');
    const start = 1;
    return email.substring(0, start) + mask + email.substring(at - 1);
  }
  return email;
}

export function alphaNumericOnly($event: {
  which: number;
  keyCode: number;
}): boolean {
  const charCode = $event.which ? $event.which : $event.keyCode;
  return (
    (charCode >= 48 && charCode <= 57) ||
    (charCode >= 65 && charCode <= 90) ||
    (charCode >= 97 && charCode <= 122)
  );
}
export function alphaNumericWithSpaceOnly($event: {
  which: number;
  keyCode: number;
}): boolean {
  const charCode = $event.which ? $event.which : $event.keyCode;
  return alphaNumericOnly($event) || charCode === 32 || charCode === 38;
}

export function floatingPointNumbersOnly($event: {
  which: number;
  keyCode: number;
}): boolean {
  const charCode = $event.which ? $event.which : $event.keyCode;
  return (charCode >= 48 && charCode <= 57) || charCode === 46;
}

export function validateName($event: {
  which: number;
  keyCode: number;
}): boolean {
  const pattern = /^[a-zA-Z.' -]+$/;
  return pattern.test(String.fromCharCode($event.keyCode));
}

export function encryptInquiryData(data: string) {
  return CryptoJS.AES.encrypt(data, esKey).toString();
}
export function generateControlKey(
  contentId: string,
  contentType: string,
  ssoId: string,
  sharedSecret: string,
  expirationTimeStamp: string,
) {
  const hashKey = CryptoJS.SHA256(
    `${contentType}${contentId}${expirationTimeStamp}${ssoId}${sharedSecret}`,
  );
  return CryptoJS.enc.Base64.stringify(hashKey).replace('+', '');
}

export function zeroValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    if (control.value.startsWith('0')) {
      return { invalidZeroControl: true };
    }
    return null;
  };
  // flag ? null : { invalidControl: true };
}

export function checkValidFirstNameLastName(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    let result = false;
    const firstNameRegEx =
      /^(?!.*[ ]{2})(?!.*?["]{2})(?!.*?[']{2})(?!.*?[.]{2})(?!.*?[-]{2})(?!.*?[,]{2})[a-zA-Z "'.,-]*$/;
    result = firstNameRegEx.test(control.value);
    return result ? null : { invalidName: true };
  };
}
